<template>
  <div v-if="error" class="p-8 text-red-600 text-lg">{{ error }}</div>
  <div v-else-if="loading" class="p-8 text-gray-600 text-lg">
    Daten werden geladen...
  </div>
  <div v-else>
    <cover-page-preview
      :total-doc-pages="totalDocPages"
      :profile-image-url="profileImageBase64"
    />
    <cv-preview
      ref="cvPreviewRef"
      :total-doc-pages="totalDocPages"
      :profile-image-url="profileImageBase64"
    />
    <motivation-preview :total-doc-pages="totalDocPages" />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick, onMounted, onBeforeUnmount } from 'vue'
import { useRoute } from 'vue-router'
import accountStore from '@/stores/account.store'
import documentsStore from '@/stores/documents.store'
import mobilityStore from '@/stores/mobility.store'
import cvStore from '@/units/ds/stores/cv.store'
import applicationPortfolioClient from '@/units/ds/services/application-portfolio.client'
import CoverPagePreview from '@/units/ds/components/documents-preview/CoverPagePreview.vue'
import CvPreview from '@/units/ds/components/documents-preview/CvPreview.vue'
import MotivationPreview from '@/units/ds/components/documents-preview/MotivationPreview.vue'
import { DocumentType } from '@/enums'

const route = useRoute()

const loading = ref(true)
const error = ref<string | null>(null)
const profileImageBase64 = ref<string | undefined>(undefined)
const cvPreviewRef = ref<InstanceType<typeof CvPreview>>()

const cvPageCount = computed(() => cvPreviewRef.value?.pages?.length ?? 1)
const totalDocPages = computed(() => 1 + cvPageCount.value + 1)

const getSingleQueryValue = (value: unknown): string | undefined =>
  Array.isArray(value)
    ? typeof value[0] === 'string'
      ? value[0]
      : undefined
    : typeof value === 'string'
      ? value
      : undefined

watch(
  () => cvPreviewRef.value?.phase,
  async (phase) => {
    if (phase === 'ready') {
      await nextTick()
      document.documentElement.classList.add('pdf-ready')

      // page-break-after: always on the last element would create a trailing blank page.
      const allPages = document.querySelectorAll('[data-a4-page]')
      allPages[allPages.length - 1]?.classList.add('is-last-page')

      await new Promise((resolve) => requestAnimationFrame(resolve))
      await new Promise((resolve) => requestAnimationFrame(resolve))
      ;(window as any).__APPLICATION_PORTFOLIO_READY = true
    }
  },
)

onBeforeUnmount(() => {
  document.documentElement.classList.remove('pdf-ready')
  document
    .querySelectorAll('[data-a4-page].is-last-page')
    .forEach((el) => el.classList.remove('is-last-page'))
  delete (window as unknown as Record<string, unknown>)
    .__APPLICATION_PORTFOLIO_READY
  delete (window as unknown as Record<string, unknown>)
    .__APPLICATION_PORTFOLIO_ERROR
})

const reportError = (message: string): void => {
  error.value = message
  loading.value = false
  ;(window as any).__APPLICATION_PORTFOLIO_ERROR = message
}

const describeLoadError = (e: any): string => {
  const status = e?.response?.status
  const responseMessage = e?.response?.data?.message
  const responseBody =
    typeof e?.response?.data === 'string' ? e.response.data : undefined
  const base = responseMessage ?? e?.message ?? 'Failed to load portfolio data'

  if (status) {
    const detail = responseBody ? ` — ${responseBody.slice(0, 200)}` : ''
    return `${base} (HTTP ${status}${detail})`
  }

  return base
}

onMounted(async () => {
  const token = getSingleQueryValue(route.query.token)
  const color = getSingleQueryValue(route.query.color) || 'orange'

  if (!token) {
    reportError('Missing render token')
    return
  }

  cvStore.setCvColor(color)

  try {
    const data = await applicationPortfolioClient.getRenderData(token)

    const account = data.account
    account.languageSkills = account.languageSkills ?? []
    account.hardSkills = account.hardSkills ?? []
    account.softSkills = account.softSkills ?? []
    account.hobbies = account.hobbies ?? []
    account.studies = account.studies ?? []
    account.apprenticeships = account.apprenticeships ?? []
    account.practicalExperiences = account.practicalExperiences ?? []

    accountStore.setAccountData(account)
    // Drop Picture-category docs: the preview components would otherwise fall
    // back to <image-preview> which fires an auth-protected request. In the
    // render context there is no logged-in session, so the fetch 401s and
    // leaves the page emitting unhandled rejections. profileImageBase64 (when
    // the backend provides it) is used directly via the <img> branch.
    documentsStore.setDocuments(
      (data.documents ?? []).filter(
        (doc) => doc.category !== DocumentType.Picture,
      ),
    )
    profileImageBase64.value = data.profileImageBase64 ?? undefined

    if (data.mobility) {
      mobilityStore.setMobility(data.mobility)
    }

    loading.value = false
  } catch (e: any) {
    console.error('Failed to load render data', e)
    reportError(describeLoadError(e))
  }
})
</script>

<style>
html,
body {
  margin: 0;
  padding: 0;
}

html.pdf-ready [data-a4-page] {
  page-break-after: always;
}
html.pdf-ready [data-a4-page].is-last-page {
  page-break-after: auto;
}

/* Only zero out the gap on flex containers that directly hold [data-a4-page]
   siblings — don't break internal flex-col layouts within page content. */
html.pdf-ready .flex-col:has(> [data-a4-page]) {
  gap: 0 !important;
}
</style>
